import {
  Blocks,
  Events,
  Extensions,
  FieldDropdown,
  FieldLabel,
  type Block,
} from 'blockly/core';

import { getBeginnerPinOptions } from '../pins/pin-selector.js';
import {
  BEGINNER_DISPLAY_BLOCK_STYLE,
  LCD_CONNECTION_I2C,
  LCD_CONNECTION_PARALLEL,
} from './display-helpers.js';
import { boardSupportsLcdParallel, getLcdI2cUi } from './lcd-i2c-ui.js';

const DETAILS_INPUT = 'DETAILS';
const LCD_CONFIGURE_UI_EXTENSION = 'beginner_lcd_configure_ui';

const PARALLEL_PIN_DEFS: Array<{ label: string; name: string }> = [
  { label: 'RS', name: 'RS_PIN' },
  { label: 'E', name: 'EN_PIN' },
  { label: 'D4', name: 'D4_PIN' },
  { label: 'D5', name: 'D5_PIN' },
  { label: 'D6', name: 'D6_PIN' },
  { label: 'D7', name: 'D7_PIN' },
];

const DETAIL_FIELD_NAMES = [
  ...PARALLEL_PIN_DEFS.map((pin) => pin.name),
  'SCL_PIN',
  'SDA_PIN',
] as const;

type LcdConfigureBlock = Block & { lcdDetailsMode?: string };

function pinDropdown(): FieldDropdown {
  return new FieldDropdown(() => getBeginnerPinOptions('digital'));
}

function snapshotDetailFields(block: Block): Record<string, string> {
  const saved: Record<string, string> = {};
  for (const name of DETAIL_FIELD_NAMES) {
    const value = block.getFieldValue(name);
    if (value != null && value !== '') {
      saved[name] = String(value);
    }
  }
  return saved;
}

function removeDetailsInput(block: Block): void {
  if (block.getInput(DETAILS_INPUT)) {
    block.removeInput(DETAILS_INPUT);
  }
}

function appendParallelDetails(block: Block, saved: Record<string, string>): void {
  const input = block.appendDummyInput(DETAILS_INPUT);
  for (const pin of PARALLEL_PIN_DEFS) {
    const field = pinDropdown();
    input.appendField(pin.label).appendField(field, pin.name);
    if (saved[pin.name]) {
      field.setValue(saved[pin.name]!);
    }
  }
}

function appendI2cDetails(block: Block, saved: Record<string, string>): void {
  const input = block.appendDummyInput(DETAILS_INPUT);
  const ui = getLcdI2cUi();

  if (ui.mode === 'fixed') {
    input.appendField(new FieldLabel(ui.label));
    return;
  }

  const scl = pinDropdown();
  const sda = pinDropdown();
  input.appendField('SCL').appendField(scl, 'SCL_PIN').appendField('SDA').appendField(sda, 'SDA_PIN');
  if (saved.SCL_PIN) {
    scl.setValue(saved.SCL_PIN);
  }
  if (saved.SDA_PIN) {
    sda.setValue(saved.SDA_PIN);
  }
}

function getConnectionOptions(): [string, string][] {
  if (!boardSupportsLcdParallel()) {
    return [['I2C', LCD_CONNECTION_I2C]];
  }
  return [
    ['Parallel', LCD_CONNECTION_PARALLEL],
    ['I2C', LCD_CONNECTION_I2C],
  ];
}

/** Skyrover forces I2C; other boards keep the requested connection. */
function resolveConnectionMode(connection?: string): string {
  if (!boardSupportsLcdParallel()) {
    return LCD_CONNECTION_I2C;
  }
  if (connection === LCD_CONNECTION_I2C || connection === LCD_CONNECTION_PARALLEL) {
    return connection;
  }
  return LCD_CONNECTION_PARALLEL;
}

/**
 * Rebuilds Parallel pin fields or I2C labels/dropdowns.
 * Must not run inside a FieldDropdown validator — that cancels the selection.
 */
export function updateLcdConfigureDetails(
  block: Block,
  connection?: string,
  force = false,
): void {
  if (block.isDisposed()) {
    return;
  }

  const requested =
    connection ?? String(block.getFieldValue('CONNECTION') ?? LCD_CONNECTION_PARALLEL);
  const mode = resolveConnectionMode(requested);
  const lcdBlock = block as LcdConfigureBlock;

  // Only force the field when Parallel is unavailable (Skyrover).
  if (!boardSupportsLcdParallel() && block.getFieldValue('CONNECTION') !== mode) {
    block.setFieldValue(mode, 'CONNECTION');
  }

  if (!force && lcdBlock.lcdDetailsMode === mode && block.getInput(DETAILS_INPUT)) {
    return;
  }

  const saved = snapshotDetailFields(block);
  removeDetailsInput(block);

  if (mode === LCD_CONNECTION_I2C) {
    appendI2cDetails(block, saved);
  } else {
    appendParallelDetails(block, saved);
  }

  lcdBlock.lcdDetailsMode = mode;
}

/** Defer shape rebuild until after the CONNECTION field value has committed. */
function scheduleLcdConfigureDetailsUpdate(block: Block, connection?: string): void {
  queueMicrotask(() => {
    if (!block.isDisposed()) {
      updateLcdConfigureDetails(block, connection, true);
    }
  });
}

function registerLcdConfigureUiExtension(): void {
  if (Extensions.isRegistered(LCD_CONFIGURE_UI_EXTENSION)) {
    return;
  }

  Extensions.register(LCD_CONFIGURE_UI_EXTENSION, function (this: Block) {
    this.setOnChange((event) => {
      if (event.type === Events.FINISHED_LOADING) {
        scheduleLcdConfigureDetailsUpdate(this);
        return;
      }

      if (
        event.type === Events.BLOCK_CHANGE &&
        'blockId' in event &&
        event.blockId === this.id &&
        'name' in event &&
        event.name === 'CONNECTION'
      ) {
        const newValue =
          'newValue' in event && event.newValue != null ? String(event.newValue) : undefined;
        scheduleLcdConfigureDetailsUpdate(this, newValue);
      }
    });
  });
}

let registered = false;

/** Registers the dynamic Configure LCD block (no device dropdown — one LCD only). */
export function registerBeginnerLcdConfigureBlock(): void {
  if (registered || Blocks['beginner_lcd_configure']) {
    registered = true;
    return;
  }

  registerLcdConfigureUiExtension();

  Blocks['beginner_lcd_configure'] = {
    init(this: Block) {
      const defaultConnection = boardSupportsLcdParallel()
        ? LCD_CONNECTION_PARALLEL
        : LCD_CONNECTION_I2C;

      this.appendDummyInput('HEADER')
        .appendField('configure LCD connection')
        .appendField(
          // Validator must only return the value — never rebuild inputs here.
          new FieldDropdown(getConnectionOptions, (newValue) =>
            resolveConnectionMode(String(newValue ?? '')),
          ),
          'CONNECTION',
        );
      this.setFieldValue(defaultConnection, 'CONNECTION');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setStyle(BEGINNER_DISPLAY_BLOCK_STYLE);
      this.setTooltip(
        boardSupportsLcdParallel()
          ? 'Sets up a 16x2 LCD. Choose Parallel to pick pins, or I2C for a plug-in module.'
          : 'Sets up a 16x2 I2C LCD for Skyrover.',
      );
      this.setHelpUrl('');
      Extensions.apply(LCD_CONFIGURE_UI_EXTENSION, this, false);
      updateLcdConfigureDetails(this, defaultConnection, true);
    },
  };

  registered = true;
}
